"use client";

import React, { useMemo, useState, useId, useEffect, useRef } from "react";
import Image from "next/image";
import { Header } from "@/components/quiz/explainers/header";
import { Button } from "@workspace/ui/components/button";
import explainersJson from "@/lib/data/explainers.json";

type CycleValue = { title: string; color: string };
type CycleItem = {
  cycle_title: string;
  date: string;
  title: string;
  description: string;
  image: string;
  values: CycleValue[];
};
type RegularCycleData = {
  id: "regular-cycle-analysis-4";
  title: string;
  cycle: CycleItem[];
};

interface Props {
  title: string;
  image?: string;
  handleNext: () => void;
}

export default function RegularCycleExplainer4({
  title,
  image,
  handleNext,
}: Props) {
  const model = useMemo(() => {
    const all = (explainersJson as any)?.explainers ?? [];
    return all.find(
      (e: any) => e?.id === "regular-cycle-analysis-4"
    ) as RegularCycleData;
  }, []);

  const DURATION_MS = 5000;

  const [active, setActive] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [progress, setProgress] = useState(0);

  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  const safeIndex = active >= 0 && active < model.cycle.length ? active : 0;
  const current = model.cycle[safeIndex];
  const panelBaseId = useId();
  const totalTabs = model.cycle.length;

  const tabs = useMemo(
    () =>
      model.cycle.map((c, idx) => ({
        id: `${model.id}-tab-${idx}`,
        label: c.cycle_title,
      })),
    [model.cycle, model.id]
  );

  useEffect(() => {
    if (!autoPlay) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setProgress(0);
      return;
    }

    startRef.current = null;

    const tick = (now: number) => {
      if (!startRef.current) startRef.current = now;
      const elapsed = now - startRef.current;
      const p = Math.min(1, elapsed / DURATION_MS);
      setProgress(p);

      if (p >= 1) {
        setActive((prev) => (prev + 1) % totalTabs);
        setProgress(0);
        startRef.current = now;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [autoPlay, totalTabs, active]);

  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [selSize, setSelSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = btnRefs.current[safeIndex];
    if (!el) return;

    const update = () => {
      const r = el.getBoundingClientRect();
      setSelSize({ w: r.width, h: r.height });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [safeIndex, tabs.length]);

  const buildPathD = (w: number, h: number) => {
    if (w <= 0 || h <= 0) return "";
    const R = 16;
    const SW = 2;
    const T = SW / 2;

    const left = T;
    const right = w - T;
    const top = T;
    const bottom = h - T;

    const maxR = Math.min(right - left, bottom - top) / 2;
    const r = Math.max(1, Math.min(R, maxR));

    const d = [
      `M ${(left + right) / 2} ${top}`,
      `L ${right - r} ${top}`,
      `A ${r} ${r} 0 0 1 ${right} ${top + r}`,
      `L ${right} ${bottom - r}`,
      `A ${r} ${r} 0 0 1 ${right - r} ${bottom}`,
      `L ${left + r} ${bottom}`,
      `A ${r} ${r} 0 0 1 ${left} ${bottom - r}`,
      `L ${left} ${top + r}`,
      `A ${r} ${r} 0 0 1 ${left + r} ${top}`,
      `L ${(left + right) / 2} ${top}`,
    ].join(" ");
    return d;
  };

  const pathD = buildPathD(selSize.w, selSize.h);

  return (
    <>
      <div className="flex flex-col flex-1 gap-[22px] overflow-y-auto min-h-0 pb-12">
        <Header title={title} />

        <div className="flex flex-col">
          <div className="rounded-t-[16px] bg-[#F3F4ED] p-5 pb-0">
            <div
              role="tablist"
              aria-label="Cycle selector"
              className="flex justify-between gap-2">
              {tabs.map((t, idx) => {
                const selected = idx === safeIndex;
                return (
                  <button
                    ref={(el) => { btnRefs.current[idx] = el; }}
                    key={t.id}
                    id={t.id}
                    role="tab"
                    aria-selected={selected}
                    aria-controls={`${panelBaseId}-panel`}
                    tabIndex={selected ? 0 : -1}
                    onClick={() => {
                      setAutoPlay(false);
                      setProgress(0);
                      setActive(idx);
                    }}
                    className={[
                      "relative",
                      "flex-1 h-[32px] rounded-[16px] border captitle-10 transition",
                      selected
                        ? "bg-white border-transparent"
                        : "bg-transparent border-[#E1E3D7] hover:bg-white/60",
                    ].join(" ")}>
                    {selected && selSize.w > 0 && selSize.h > 0 && (
                      <svg
                        className="absolute inset-0 pointer-events-none"
                        width="100%"
                        height="100%"
                        viewBox={`0 0 ${selSize.w} ${selSize.h}`}
                        preserveAspectRatio="none">
                        <path
                          d={pathD}
                          fill="none"
                          stroke="rgba(54,84,93,0.15)"
                          strokeWidth={2}
                          vectorEffect="non-scaling-stroke"
                        />

                        {autoPlay ? (
                          <path
                            d={pathD}
                            fill="none"
                            stroke="#36545D"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            pathLength={1}
                            strokeDasharray={`${progress} ${1 - progress}`}
                            strokeDashoffset={0}
                          />
                        ) : (
                          <path
                            d={pathD}
                            fill="none"
                            stroke="#36545D"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                          />
                        )}
                      </svg>
                    )}

                    <span className="relative z-[1]">{t.label}</span>
                  </button>
                );
              })}
            </div>

            <div
              id={`${panelBaseId}-panel`}
              role="tabpanel"
              aria-labelledby={tabs[safeIndex]?.id}
              className="mt-5">
              <p className="captitle-10 text-[#78848A]">
                {current?.date ?? ""}
              </p>

              <p className="mt-1 title-20 text-[#112D35]">
                {current?.title ?? ""}
              </p>

              <p
                className="mt-2 title-12 text-[#78848A]"
                dangerouslySetInnerHTML={{ __html: current?.description ?? "" }}
              />

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                {current?.values?.map((v, i) => (
                  <span
                    key={`${v.title}-${i}`}
                    className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block"
                      style={{
                        width: 11,
                        height: 11,
                        borderRadius: 2,
                        backgroundColor: v.color,
                      }}
                    />
                    <span className="captitle-10 normal-case text-[#122D35]">
                      {v.title}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-0">
            <div className="relative w-full h-[264px] md:h-[346px] rounded-b-[16px] overflow-hidden bg-[#F3F4ED]">
              <Image
                src={current?.image ?? ""}
                alt={`${current?.cycle_title ?? "" } chart`}
                fill
                className="object-contain object-bottom"
                sizes="100vw"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 flex flex-col gap-3 pb-6">
        <Button onClick={handleNext} variant="default" className="w-full shrink-0">Next</Button>
      </div>
    </>
  );
}
